import { Checkbox, NumberInput, Select, Textarea, TextInput } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { DayInput } from '../../inputs/DayInput.js';
import classes from './Hse.module.css';
import type { HseReportInput } from './types.js';

/**
 * Rapor formunun alanları (ADR 0049 §1): tanımla çizilir. Değer yolu `details.psc.port`
 * biçimindedir; alan hatası domain şemasının yolundan gelir (`parseHseReport`).
 */

export type FieldKind = 'text' | 'textarea' | 'select' | 'date' | 'number' | 'bool';

export interface FieldSpec {
  path: string;
  kind: FieldKind;
  /** Seçenek değerleri; etiketleri `hse.<optionsKey>.<değer>`. */
  options?: readonly string[];
  optionsKey?: string;
  /** Hazır etiketli seçenekler (ekipman, kişi). */
  choices?: readonly { value: string; label: string }[];
  required?: boolean;
  /** Etiket anahtarı `hse.fields.<label>`; verilmezse yolun son parçası. */
  label?: string;
  full?: boolean;
  max?: number;
  rows?: number;
  /** Seçenek değeri sayıdır (olasılık, şiddet; ADR 0097 §4). */
  numeric?: boolean;
}

export function getPath(value: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>(
      (node, key) =>
        node !== null && typeof node === 'object'
          ? (node as Record<string, unknown>)[key]
          : undefined,
      value,
    );
}

export function setPath<T>(value: T, path: string, next: unknown): T {
  const [head, ...rest] = path.split('.');
  const node = (value ?? {}) as Record<string, unknown>;
  if (head === undefined) return value;
  return {
    ...node,
    [head]: rest.length === 0 ? next : setPath(node[head] ?? {}, rest.join('.'), next),
  } as T;
}

export interface FieldsProps {
  specs: readonly FieldSpec[];
  value: HseReportInput;
  onChange: (next: HseReportInput) => void;
  readOnly: boolean;
  errors: Readonly<Record<string, string>>;
  columns?: 2 | 3;
  /**
   * Kilitli alanlar (sağlık verisi; ADR 0062 §1.3): değer gösterilmez ve yazılmaz; yerinde "gizli"
   * kutusu.
   */
  locked?: ReadonlySet<string>;
  /**
   * Değeri görünen ama bu kişinin yazamadığı alanlar (kaptana ait alan; ADR 0127 §2): nedeni
   * alanın altındaki notta.
   */
  readOnlyPaths?: ReadonlySet<string>;
}

/** Alan grubu: üç (ya da iki) sütunlu ızgara; dar ekranda tek sütun. */
export function Fields({
  specs,
  value,
  onChange,
  readOnly,
  errors,
  columns = 3,
  locked,
  readOnlyPaths,
}: FieldsProps) {
  const { t } = useTranslation();
  return (
    <div className={columns === 3 ? classes.grid3 : classes.grid2}>
      {specs.map((spec) => (
        <div key={spec.path} className={spec.full ? classes.full : undefined}>
          {locked?.has(spec.path) ? (
            <TextInput
              label={t(`hse.fields.${spec.label ?? spec.path.split('.').pop()}`)}
              readOnly
              value={t('hse.medical.restricted')}
              leftSection={<IconLock size={14} aria-hidden />}
              data-restricted="true"
            />
          ) : (
            <Field
              spec={spec}
              value={getPath(value, spec.path)}
              readOnly={readOnly || readOnlyPaths?.has(spec.path) === true}
              error={errors[spec.path] ? t(`hse.fieldErrors.${errors[spec.path]}`) : undefined}
              onChange={(next) => onChange(setPath(value, spec.path, next))}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function Field({
  spec,
  value,
  readOnly,
  error,
  onChange,
}: {
  spec: FieldSpec;
  value: unknown;
  readOnly: boolean;
  error: string | undefined;
  onChange: (value: unknown) => void;
}) {
  const { t } = useTranslation();
  const label = t(`hse.fields.${spec.label ?? spec.path.split('.').pop()}`);
  const common = {
    label,
    required: spec.required ?? false,
    readOnly,
    error,
  };
  const text = typeof value === 'string' ? value : '';
  switch (spec.kind) {
    case 'textarea':
      return (
        <>
          <Textarea
            {...common}
            value={text}
            autosize
            minRows={spec.rows ?? 3}
            maxRows={12}
            maxLength={spec.max ?? 4000}
            onChange={(event) => onChange(event.currentTarget.value || null)}
          />
          {!readOnly ? (
            <div className={classes.counter} aria-hidden>
              {text.length}/{spec.max ?? 4000}
            </div>
          ) : null}
        </>
      );
    case 'select': {
      const data =
        spec.choices ??
        (spec.options ?? []).map((option) => ({
          value: option,
          label: t(`hse.${spec.optionsKey}.${option}`),
        }));
      return (
        <Select
          {...common}
          data={data}
          value={
            typeof value === 'string' ? value : typeof value === 'number' ? String(value) : null
          }
          clearable={!spec.required && !readOnly}
          searchable={data.length > 8}
          {...(readOnly ? { rightSection: null } : {})}
          onChange={(next) => onChange(next === null ? null : spec.numeric ? Number(next) : next)}
        />
      );
    }
    case 'date':
      return (
        <DayInput
          {...common}
          value={typeof value === 'string' ? value : null}
          onChange={(next) => onChange(next)}
        />
      );
    case 'number':
      return (
        <NumberInput
          {...common}
          value={typeof value === 'number' ? value : ''}
          min={0}
          max={1000}
          allowDecimal={false}
          onChange={(next) => onChange(typeof next === 'number' ? next : null)}
        />
      );
    case 'bool':
      return (
        <Checkbox
          label={label}
          checked={value === true}
          disabled={readOnly}
          mt="lg"
          onChange={(event) => onChange(event.currentTarget.checked)}
        />
      );
    default:
      return (
        <TextInput
          {...common}
          value={text}
          maxLength={spec.max ?? 200}
          onChange={(event) => onChange(event.currentTarget.value || null)}
        />
      );
  }
}
